import { useState, useEffect } from "react";
import {
    Box, Typography, Button, Stack, Divider,
    CircularProgress, Chip
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import TuneIcon from "@mui/icons-material/Tune";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReceiveStockDialog from "./ReceiveStockDialog";
import IssueStockDialog from "./IssueStockDialog";
import AdjustStockDialog from "./AdjustStockDialog";
import { getInventoryItems, getInventoryLogs } from "./InventoryApi";

const InventoryPage = () => {

    const [receiveOpen, setReceiveOpen]   = useState(false);
    const [issueOpen, setIssueOpen]       = useState(false);
    const [adjustOpen, setAdjustOpen]     = useState(false);
    const [lowStockItems, setLowStockItems] = useState([]);
    const [todayLogs, setTodayLogs]       = useState([]);
    const [loading, setLoading]           = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        const today = new Date().toISOString().split("T")[0];
        const [items, logs] = await Promise.all([
            getInventoryItems(),
            getInventoryLogs({ date: today }),
        ]);
        setLowStockItems((items || []).filter(i => Number(i.current_stock) <= Number(i.min_stock)));
        setTodayLogs(logs || []);
        setLoading(false);
    };

    const handleSuccess = () => fetchData();

    return (
        <Box p={3} maxWidth={600} mx="auto">

            {/* Header */}
            <Typography variant="h5" fontWeight={700} mb={3}>
                Inventory
            </Typography>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} mb={3}>
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => setReceiveOpen(true)}
                    fullWidth
                    size="large"
                >
                    Receive Stock
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    startIcon={<RemoveCircleOutlineIcon />}
                    onClick={() => setIssueOpen(true)}
                    fullWidth
                    size="large"
                >
                    Issue Stock
                </Button>
            </Stack>

            {loading ? (
                <Box display="flex" justifyContent="center" py={5}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Low Stock Alerts */}
                    {lowStockItems.length > 0 && (
                        <Box mb={3}>
                            <Typography variant="subtitle2" color="warning.main" fontWeight={700} mb={1}>
                                ⚠️ Low Stock Alerts
                            </Typography>
                            <Stack spacing={1}>
                                {lowStockItems.map((item) => (
                                    <Box
                                        key={item.id}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        bgcolor="#fff3e0"
                                        borderRadius={1}
                                        px={2}
                                        py={1}
                                        border="1px solid"
                                        borderColor="warning.light"
                                    >
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <WarningAmberIcon fontSize="small" color="warning" />
                                            <Typography variant="body2" fontWeight={600}>
                                                {item.name}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="warning.main" fontWeight={700}>
                                            {item.current_stock} remaining
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Today's Activity */}
                    <Box mb={3}>
                        <Typography variant="subtitle2" fontWeight={700} mb={1}>
                            Today's Activity
                        </Typography>
                        {todayLogs.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No activity recorded today.
                            </Typography>
                        ) : (
                            <Stack spacing={1}>
                                {todayLogs.map((log) => (
                                    <Box
                                        key={log.id}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        bgcolor="#fafafa"
                                        borderRadius={1}
                                        px={2}
                                        py={1}
                                        border="1px solid"
                                        borderColor="divider"
                                    >
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>
                                                {log.item_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {log.description} · {new Date(log.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={`${log.type === "IN" ? "+" : "-"}${log.quantity}`}
                                            size="small"
                                            color={log.type === "IN" ? "success" : "error"}
                                            variant="outlined"
                                        />
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Adjust Stock CTA */}
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        bgcolor="#f5f5f5"
                        borderRadius={1}
                        px={2}
                        py={1.5}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Need to correct the stock?
                        </Typography>
                        <Button
                            size="small"
                            startIcon={<TuneIcon />}
                            onClick={() => setAdjustOpen(true)}
                        >
                            Adjust Stock
                        </Button>
                    </Box>
                </>
            )}

            {/* Dialogs */}
            <ReceiveStockDialog
                open={receiveOpen}
                onClose={() => setReceiveOpen(false)}
                onSuccess={handleSuccess}
            />
            <IssueStockDialog
                open={issueOpen}
                onClose={() => setIssueOpen(false)}
                onSuccess={handleSuccess}
            />
            <AdjustStockDialog
                open={adjustOpen}
                onClose={() => setAdjustOpen(false)}
                onSuccess={handleSuccess}
            />
        </Box>
    );
};

export default InventoryPage;